/**
 * UAVTradeZone Modification Record
 * 
 * Change ID: PHASE_2_SCHEDULER_API
 * Date: 2025-01-03
 * Modified By: Cursor
 * 
 * Description:
 * API service for scheduler admin endpoints
 * Handles backup triggers, history, and job management
 * 
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Target: 200 lines max (Service limit)
 */

import apiClient from './apiClient';

const schedulerApi = {
  triggerBackup: async (params: {
    collections?: string[];
    include_files?: boolean;
    notification_email?: string;
  }) => {
    const response = await apiClient.post('/api/admin/scheduler/backup/trigger', params);
    return response.data;
  },

  getBackupHistory: async (params: {
    limit?: number;
    skip?: number;
    environment?: string;
  }) => {
    const response = await apiClient.get('/api/admin/scheduler/backup/history', { params });
    return response.data;
  },

  getBackupHistorySummary: async (params: { environment?: string } = {}) => {
    // [2026-01-31] Smart refresh: Lightweight token to avoid full refresh polling
    const response = await apiClient.get('/api/admin/scheduler/backup/history/summary', { params });
    return response.data;
  },

  getBackupFiles: async (params: {
    limit?: number;
    skip?: number;
    environment?: string;
  } = {}) => {
    // [2026-01-31] Backup files: Read from MongoDB index (synced by scheduler)
    const response = await apiClient.get('/api/admin/scheduler/backup/files', { params });
    return response.data;
  },

  getDbCollections: async () => {
    // [2026-01-31] Restore: Provide list of collections for selection UI
    const response = await apiClient.get('/api/admin/scheduler/db/collections');
    return response.data;
  },

  triggerRestore: async (params: {
    backup_file_name: string;
    collections: string[] | null;
  }) => {
    // [2026-01-31] Restore: Schedule restore job via scheduler_requests
    const response = await apiClient.post('/api/admin/scheduler/restore/trigger', params);
    return response.data;
  },

  triggerExchangeRates: async (params: {
    base_currency?: string;
    target_currencies?: string[];
  }) => {
    const response = await apiClient.post('/api/admin/scheduler/exchange-rates/trigger', params);
    return response.data;
  },

  getCurrentExchangeRates: async (baseCurrency = 'USD') => {
    const response = await apiClient.get('/api/admin/scheduler/exchange-rates/current', {
      params: { base_currency: baseCurrency }
    });
    return response.data;
  },

  getScheduledJobs: async (params: {
    status?: string;
    job_type?: string;
    limit?: number;
    skip?: number;
  }) => {
    const response = await apiClient.get('/api/admin/scheduler/jobs', { params });
    return response.data;
  },

  cancelJob: async (jobId: string) => {
    const response = await apiClient.delete(`/api/admin/scheduler/jobs/${jobId}`);
    return response.data;
  },

  fetchSchedulerStats: async () => {
    const response = await apiClient.get('/api/admin/scheduler/stats');
    return response.data;
  },
  // [2026-03-08] Admin API: Trigger manual signup report with optional date range
  triggerSignupReport: async (params: {
    start_date?: string;
    end_date?: string;
    notification_email?: string;
  }) => {
    const response = await apiClient.post('/api/admin/scheduler/signup-report/trigger', params);
    return response.data;
  },

  fetchSchedulerDashboard: async () => {
    // [2026-01-05] Scheduler API: Provide hero card payload
    const response = await apiClient.get('/api/admin/scheduler/dashboard');
    return response.data;
  },
};

export default schedulerApi;